import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Card, PageContainer, Section } from '../components/common';
import api from '../services/api';

/**
 * ProjectList Component
 * Debug utility page to list all projects with details
 * 
 * @returns {React.ReactElement} ProjectList component
 */
const ProjectList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await api.get('/projects');
        setProjects(response.data.data || []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message || 'Failed to fetch projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Handler for copying project ID
  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    // Show a temporary message or toast
    alert(`Copied ID: ${id}`);
  };

  return (
    <PageContainer>
      <Box className="page-title-container">
        <Typography variant="h5" component="h1" fontWeight={600}>
          Project List (Debug)
        </Typography>
      </Box>
      
      <Section
        title="Project Details"
        subtitle="This is a utility page to help debug project IDs. Click on a project ID to copy it."
      >
        {loading ? (
          <Typography>Loading projects...</Typography>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : projects.length === 0 ? (
          <Typography>No projects found.</Typography>
        ) : (
          <Grid container spacing={3}>
            {projects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project._id}>
                <Card
                  title={project.name}
                  subtitle={`Status: ${project.status}`}
                  actions={
                    <Button 
                      variant="contained" 
                      size="small" 
                      onClick={() => navigate(`/projects/${project._id}`)}
                    >
                      View
                    </Button>
                  }
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                      <strong>ID:</strong> 
                      <Box 
                        component="span" 
                        sx={{ 
                          fontFamily: 'monospace',
                          display: 'inline-flex',
                          alignItems: 'center',
                          ml: 1
                        }}
                      >
                        {project._id}
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<ContentCopyIcon fontSize="small" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyId(project._id);
                          }}
                          sx={{ ml: 1 }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Created:</strong> {new Date(project.createdAt).toLocaleString()}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Updated:</strong> {new Date(project.updatedAt).toLocaleString()}
                    </Typography>
                    
                    {project.description && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Description:</strong> {project.description}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Section>
    </PageContainer>
  );
};

export default ProjectList;